/**
 * Phase 3G: Export Package Generator
 * 
 * Generates export packages in three formats:
 * 1. EPIC_COPY_PASTE — Clean, copyable block for EHR entry
 * 2. PDF_SUMMARY — Printable summary with attestation, timeline, evidence
 * 3. JSON — Structured payload for future integration
 * 
 * HARD BOUNDARIES:
 * - LINK does NOT submit claims
 * - Export is the final step — output is for hospital billing systems
 * - Demo exports are clearly labeled "Illustrative Only"
 */

import { jsPDF } from 'jspdf';

// ============================================================================
// TYPES
// ============================================================================

export interface ExportCptLine {
  sequence: number;
  cptCode: string;
  cptDescription?: string;
  modifiers: string[];
  units: number;
  finalCodeString: string;
  modifierExplanations: Record<string, string>;
}

export interface ExportAuditEvent {
  timestamp: string;
  action: string;
  actor?: string;
  details?: string;
}

export interface ExportPackageInput {
  caseNumber: string;
  authReference?: string;
  patientId?: string;
  surgeonName?: string;
  facilityName?: string;
  scheduledDate?: string;
  attestationText?: string;
  attestationTimestamp?: string;
  cptLines: ExportCptLine[];
  auditTimeline: ExportAuditEvent[];
  evidenceSummary?: string[];
  isDemoExport: boolean;
  generatedAt: string;
  generatedBy?: string;
}

// ============================================================================
// EPIC COPY/PASTE FORMAT
// ============================================================================

/**
 * Generate a clean, copyable text block for Epic EHR entry.
 */
export function generateEpicCopyPaste(input: ExportPackageInput): string {
  const lines: string[] = [];
  const divider = '─'.repeat(60);

  if (input.isDemoExport) {
    lines.push('╔══════════════════════════════════════════════════════════╗');
    lines.push('║   DEMO MODE — ILLUSTRATIVE ONLY — NO CLAIMS SUBMITTED  ║');
    lines.push('╚══════════════════════════════════════════════════════════╝');
    lines.push('');
  }

  lines.push('LINK — Closed-Loop Revenue Export');
  lines.push(divider);
  lines.push('');

  // Header
  lines.push(`Case:           ${input.caseNumber}`);
  if (input.authReference) {
    lines.push(`Auth Reference: ${input.authReference}`);
  }
  if (input.scheduledDate) {
    lines.push(`Date of Service: ${input.scheduledDate}`);
  }
  if (input.surgeonName) {
    lines.push(`Surgeon:         ${input.surgeonName}`);
  }
  lines.push(`Generated:       ${new Date(input.generatedAt).toLocaleString()}`);
  lines.push('');
  lines.push(divider);
  lines.push('FINAL CPT LINES');
  lines.push(divider);
  lines.push('');

  // CPT Lines
  lines.push('Seq  Code       Modifiers    Units  Description');
  lines.push('───  ─────────  ──────────   ─────  ───────────────────────');
  
  for (const cpt of input.cptLines) {
    const seq = String(cpt.sequence).padEnd(5);
    const code = cpt.finalCodeString.padEnd(11);
    const mods = cpt.modifiers.length > 0 ? cpt.modifiers.join(', ').padEnd(13) : '—'.padEnd(13);
    const units = String(cpt.units).padEnd(7);
    const desc = cpt.cptDescription || '';
    lines.push(`${seq}${code}${mods}${units}${desc}`);
  }

  lines.push('');
  lines.push(divider);

  // Attestation
  if (input.attestationText) {
    lines.push('SURGEON ATTESTATION');
    lines.push(divider);
    lines.push(input.attestationText);
    if (input.attestationTimestamp) {
      lines.push(`Attested: ${new Date(input.attestationTimestamp).toLocaleString()}`);
    }
    lines.push('');
  }

  // Modifier explanations
  const hasExplanations = input.cptLines.some(
    l => Object.keys(l.modifierExplanations).length > 0
  );
  if (hasExplanations) {
    lines.push(divider);
    lines.push('MODIFIER RATIONALE');
    lines.push(divider);
    for (const cpt of input.cptLines) {
      for (const [mod, reason] of Object.entries(cpt.modifierExplanations)) {
        lines.push(`  CPT ${cpt.cptCode} → ${mod}: ${reason}`);
      }
    }
    lines.push('');
  }

  // Footer
  lines.push(divider);
  lines.push('Export generated by LINK Revenue Integrity System');
  lines.push('This export does not constitute a claim submission.');
  if (input.isDemoExport) {
    lines.push('*** DEMO DATA — NOT FOR CLINICAL OR BILLING USE ***');
  }

  return lines.join('\n');
}

// ============================================================================
// PDF SUMMARY FORMAT
// ============================================================================

/**
 * Generate a PDF summary document.
 */
export function generatePdfSummary(input: ExportPackageInput): jsPDF {
  const doc = new jsPDF();
  let y = 20;
  const leftMargin = 15;
  const pageWidth = doc.internal.pageSize.getWidth();

  const addLine = (text: string, fontSize = 10, isBold = false) => {
    if (y > 270) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.text(text, leftMargin, y);
    y += fontSize * 0.5 + 2;
  };

  const addDivider = () => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setDrawColor(200);
    doc.line(leftMargin, y, pageWidth - leftMargin, y);
    y += 5;
  };

  // Demo banner
  if (input.isDemoExport) {
    doc.setFillColor(255, 243, 205);
    doc.rect(0, 0, pageWidth, 15, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(180, 130, 0);
    doc.text('DEMO MODE — ILLUSTRATIVE ONLY — NO CLAIMS SUBMITTED', pageWidth / 2, 10, { align: 'center' });
    doc.setTextColor(0);
    y = 25;
  }

  // Title
  addLine('LINK — Closed-Loop Revenue Export', 16, true);
  y += 3;
  addDivider();

  // Case info
  addLine('Case Information', 12, true);
  addLine(`Case Number: ${input.caseNumber}`);
  if (input.authReference) addLine(`Authorization: ${input.authReference}`);
  if (input.scheduledDate) addLine(`Date of Service: ${input.scheduledDate}`);
  if (input.surgeonName) addLine(`Surgeon: ${input.surgeonName}`);
  if (input.facilityName) addLine(`Facility: ${input.facilityName}`);
  addLine(`Generated: ${new Date(input.generatedAt).toLocaleString()}`);
  y += 3;
  addDivider();

  // Attestation
  if (input.attestationText) {
    addLine('Surgeon Attestation', 12, true);
    // Word-wrap attestation text
    const splitText = doc.splitTextToSize(input.attestationText, pageWidth - 2 * leftMargin);
    for (const line of splitText) {
      addLine(line, 9);
    }
    if (input.attestationTimestamp) {
      addLine(`Attested: ${new Date(input.attestationTimestamp).toLocaleString()}`, 8);
    }
    y += 3;
    addDivider();
  }

  // CPT Lines Table
  addLine('Final CPT Lines', 12, true);
  y += 2;

  // Table header
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('Seq', leftMargin, y);
  doc.text('Code', leftMargin + 15, y);
  doc.text('Modifiers', leftMargin + 45, y);
  doc.text('Units', leftMargin + 80, y);
  doc.text('Description', leftMargin + 100, y);
  y += 5;
  doc.setDrawColor(150);
  doc.line(leftMargin, y - 2, pageWidth - leftMargin, y - 2);

  // Table rows
  doc.setFont('helvetica', 'normal');
  for (const cpt of input.cptLines) {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.text(String(cpt.sequence), leftMargin, y);
    doc.text(cpt.finalCodeString, leftMargin + 15, y);
    doc.text(cpt.modifiers.join(', ') || '—', leftMargin + 45, y);
    doc.text(String(cpt.units), leftMargin + 80, y);
    const desc = (cpt.cptDescription || '').substring(0, 40);
    doc.text(desc, leftMargin + 100, y);
    y += 5;
  }
  y += 3;
  addDivider();

  // Modifier Rationale
  const hasExplanations = input.cptLines.some(
    l => Object.keys(l.modifierExplanations).length > 0
  );
  if (hasExplanations) {
    addLine('Modifier Rationale', 12, true);
    for (const cpt of input.cptLines) {
      for (const [mod, reason] of Object.entries(cpt.modifierExplanations)) {
        addLine(`CPT ${cpt.cptCode} → ${mod}: ${reason}`, 8);
      }
    }
    y += 3;
    addDivider();
  }

  // Audit Timeline
  if (input.auditTimeline.length > 0) {
    addLine('Decision Timeline', 12, true);
    for (const event of input.auditTimeline.slice(0, 30)) {
      const time = new Date(event.timestamp).toLocaleString();
      addLine(`${time} — ${event.action}${event.details ? ': ' + event.details : ''}`, 7);
    }
    if (input.auditTimeline.length > 30) {
      addLine(`... and ${input.auditTimeline.length - 30} more events`, 7);
    }
    y += 3;
    addDivider();
  }

  // Footer
  y += 5;
  addLine('Export generated by LINK Revenue Integrity System', 8);
  addLine('This export does not constitute a claim submission.', 8);
  if (input.isDemoExport) {
    doc.setTextColor(180, 130, 0);
    addLine('DEMO DATA — NOT FOR CLINICAL OR BILLING USE', 9, true);
    doc.setTextColor(0);
  }

  return doc;
}

// ============================================================================
// JSON EXPORT FORMAT
// ============================================================================

export interface JsonExportPayload {
  version: '1.0';
  exportType: 'closed_loop_revenue';
  isDemoExport: boolean;
  generatedAt: string;
  generatedBy?: string;
  case: {
    caseNumber: string;
    authReference?: string;
    patientId?: string;
    surgeonName?: string;
    facilityName?: string;
    scheduledDate?: string;
  };
  attestation: {
    text?: string;
    timestamp?: string;
    exists: boolean;
  };
  cptLines: Array<{
    sequence: number;
    cptCode: string;
    description?: string;
    modifiers: string[];
    units: number;
    finalCodeString: string;
    modifierRationale: Record<string, string>;
  }>;
  auditTimeline: ExportAuditEvent[];
  metadata: {
    closedLoopValidation: 'passed' | 'demo_bypass';
    exportHash: string;
    totalCpts: number;
  };
}

/**
 * Generate a structured JSON export payload.
 */
export function generateJsonExport(input: ExportPackageInput): JsonExportPayload {
  // Generate a simple hash for integrity
  const hashInput = JSON.stringify({
    caseNumber: input.caseNumber,
    cptLines: input.cptLines.map(l => l.finalCodeString),
    timestamp: input.generatedAt,
  });
  const exportHash = btoa(hashInput).slice(0, 32);

  return {
    version: '1.0',
    exportType: 'closed_loop_revenue',
    isDemoExport: input.isDemoExport,
    generatedAt: input.generatedAt,
    generatedBy: input.generatedBy,
    case: {
      caseNumber: input.caseNumber,
      authReference: input.authReference,
      patientId: input.isDemoExport ? undefined : input.patientId,
      surgeonName: input.surgeonName,
      facilityName: input.facilityName,
      scheduledDate: input.scheduledDate,
    },
    attestation: {
      text: input.attestationText,
      timestamp: input.attestationTimestamp,
      exists: !!input.attestationText,
    },
    cptLines: input.cptLines.map(l => ({
      sequence: l.sequence,
      cptCode: l.cptCode,
      description: l.cptDescription,
      modifiers: l.modifiers,
      units: l.units,
      finalCodeString: l.finalCodeString,
      modifierRationale: l.modifierExplanations,
    })),
    auditTimeline: input.auditTimeline,
    metadata: {
      closedLoopValidation: input.isDemoExport ? 'demo_bypass' : 'passed',
      exportHash,
      totalCpts: input.cptLines.length,
    },
  };
}
